import * as React from "react";

export interface HierarchyNode {
  id: string;
  label: string;
  parent: string | null;
  level: number;
  weight?: number;
}

export interface HierarchyIndex {
  nodeById: Record<string, HierarchyNode>;
  childrenOf: Record<string, string[]>;
  isLeaf: (id: string) => boolean;
  leaves: HierarchyNode[];
  ancestorsOf: (id: string) => string[];
  maxLevel: number;
}

export function useHierarchy(nodes: HierarchyNode[]): HierarchyIndex {
  return React.useMemo(() => {
    const nodeById: Record<string, HierarchyNode> = Object.fromEntries(
      nodes.map((n) => [n.id, n]),
    );
    const childrenOf: Record<string, string[]> = {};
    nodes.forEach((n) => {
      if (n.parent) {
        (childrenOf[n.parent] = childrenOf[n.parent] || []).push(n.id);
      }
    });
    const isLeaf = (id: string) => !childrenOf[id];
    const leaves = nodes.filter((n) => isLeaf(n.id));
    const ancestorsOf = (id: string): string[] => {
      const path: string[] = [];
      let cur: HierarchyNode | undefined = nodeById[id];
      while (cur && cur.parent) {
        path.push(cur.parent);
        cur = nodeById[cur.parent];
      }
      return path;
    };
    const maxLevel = nodes.reduce((m, n) => Math.max(m, n.level), 0);
    return { nodeById, childrenOf, isLeaf, leaves, ancestorsOf, maxLevel };
  }, [nodes]);
}

export function levelLegendItems(
  nodes: HierarchyNode[],
  labels: [string, string, string] = ["Root", "Level 1", "Level 2"],
) {
  const counts: Record<number, number> = {};
  nodes.forEach((n) => {
    counts[n.level] = (counts[n.level] ?? 0) + 1;
  });
  const colors = ["var(--chart-1)", "var(--chart-2)", "var(--chart-3)"];
  return labels.map((label, i) => ({
    id: `l${i}`,
    label,
    value: counts[i] ?? 0,
    color: colors[i],
  }));
}

export const levelForFilter: Record<string, number> = { l0: 0, l1: 1, l2: 2 };

export const hierarchyDemoData: HierarchyNode[] = [
  { id: "root", label: "Organization", parent: null, level: 0, weight: 38 },
  { id: "prod", label: "Product", parent: "root", level: 1, weight: 12 },
  { id: "eng", label: "Engineering", parent: "root", level: 1, weight: 18 },
  { id: "ops", label: "Operations", parent: "root", level: 1, weight: 8 },
  { id: "ios", label: "iOS", parent: "prod", level: 2, weight: 4 },
  { id: "web", label: "Web", parent: "prod", level: 2, weight: 5 },
  { id: "des", label: "Design", parent: "prod", level: 2, weight: 3 },
  { id: "plat", label: "Platform", parent: "eng", level: 2, weight: 7 },
  { id: "ml", label: "ML", parent: "eng", level: 2, weight: 5 },
  { id: "dev", label: "DevOps", parent: "eng", level: 2, weight: 6 },
  { id: "fin", label: "Finance", parent: "ops", level: 2, weight: 3 },
  { id: "ppl", label: "People", parent: "ops", level: 2, weight: 5 },
];
